/**
 * The Security board — one board, shaped like the deck it feeds.
 *
 * There used to be two: `/security` (collector telemetry) and `/proofpoint`
 * (the executive figures). Both were about email security, both were fed by the
 * same Proofpoint credential, and a reader had to hold two mental models to
 * answer one question. They are merged here, and `/proofpoint` redirects.
 *
 * The board is named "Security" rather than "Proofpoint" on purpose: the
 * audience thinks in terms of security, not in terms of the vendor that happens
 * to supply the data today. When Defender or Cato feeds join, the name still
 * holds.
 *
 * **The structure mirrors the quarterly "IT & OT Security" leadership deck**, so
 * a reader moves between slide and board without re-orienting:
 *
 *   - **L0** — the four numbers leadership already receives, in the deck's own
 *     words: dangerous emails blocked before delivery, people individually
 *     targeted, incidents with business impact, block rate.
 *   - **Section A — "Norican, last 90 days"** is the deck card itself: the
 *     copyable statements, the trend behind them, what was blocked, and the
 *     block rate with its coverage.
 *   - **Section B — "Under pressure"**: who and what is being aimed at —
 *     attributed campaigns, the named targeted people, the cohorts the attacks
 *     land on, and the volume context that says whether the pipe is even
 *     flowing.
 *   - **Section C — "Our plan"**: control health, plus the programme figures the
 *     deck reports and NOVA cannot measure, badged as curated.
 *
 * Every card ends in a status verdict and every section in a one-line bottom
 * line — the deck's grammar, which is what turns a wall of numbers into an
 * argument. Both are slots (`components/board/verdict.tsx`,
 * `components/board/board-section.tsx`), not markup copied per panel.
 */

import { Icon, ShieldCheck } from "@norican/icons";
import { Alert, Freshness, PageHeader, Skeleton } from "@norican/react";

import { BoardSection } from "@/components/board";
import { NAV_SECTIONS } from "@/components/nav-sections";
import { useSecurityBoard } from "@/lib/api";
import { logger } from "@/lib/logger";

import { BlockRateMeter } from "./block-rate-meter";
import { BlockedTrend } from "./blocked-trend";
import { CampaignTimeline } from "./campaign-timeline";
import { ClassificationDonut } from "./classification-donut";
import { ExecTiles } from "./exec-tiles";
import { IntegrationHealth } from "./integration-health";
import { MailFlow } from "./mail-flow";
import { MessageTrend } from "./message-trend";
import { NotConfigured } from "./not-configured";
import { ProgrammeStatus } from "./programme-status";
import { QuarterSnapshot } from "./quarter-snapshot";
import { figuresAreCurrent, formatCount, staleAfterMs } from "./status";
import { TargetedCohorts } from "./targeted-cohorts";
import { TargetedPeople } from "./targeted-people";

const SECTION = NAV_SECTIONS.find((section) => section.label === "Security");

export function SecurityPage() {
  const { overview, targeted, isPending, error } = useSecurityBoard();

  if (error) {
    logger.warn("[security] board request failed", error);
  }

  const configured = overview?.configured === true;
  const health = overview?.health;
  const summary = overview?.summary;
  // A collector that has stopped, been rate-limited, or lost its store makes
  // every count below a frozen snapshot rather than a measurement. Panels that
  // show a number are told, so they can caption themselves honestly.
  const figuresCurrent = overview ? figuresAreCurrent(overview) : false;

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-8 px-6 py-8">
      <PageHeader
        title="Security"
        subtitle={
          SECTION?.description ??
          "Email security as leadership reads it: blocked, individually targeted, business impact, block rate."
        }
        actions={
          health?.last_success_at && configured ? (
            <Freshness
              label="Proofpoint"
              since={health.last_success_at}
              staleAfter={staleAfterMs(health)}
            />
          ) : undefined
        }
      />

      {error && (
        <Alert tone="danger" title="Couldn't load the security board">
          {error instanceof Error ? error.message : "The NOVA security API did not respond."}
        </Alert>
      )}

      {isPending && !overview ? (
        <div className="flex flex-col gap-4" aria-busy="true">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} height="9rem" />
            ))}
          </div>
          <Skeleton height="18rem" />
          <Skeleton height="14rem" />
        </div>
      ) : configured ? (
        <>
          {!figuresCurrent && (
            <Alert
              tone="warning"
              icon={<Icon icon={ShieldCheck} size="sm" />}
              title="These figures are not current"
            >
              Collection is not running normally, so every count below is frozen at the last
              successful poll — read them as stale rather than calm. Control health in section C
              explains why.
            </Alert>
          )}

          {overview?.errors.map((message) => (
            <Alert key={message} tone="warning" title="Partial data">
              {message}
            </Alert>
          ))}

          {/* L0 — is anything wrong right now? The four numbers leadership
              already receives, in the words they already read. */}
          {overview && summary && (
            <ExecTiles
              summary={summary}
              trend={overview.trend}
              definition={overview.definition.statement}
            />
          )}

          <BoardSection
            id="security-last-90-days"
            eyebrow="Section A"
            title="Norican, last 90 days"
            lead="The deck's own card, recomputable. What the controls stopped, what got through, and how much of the window the figures were measured over."
            bottomLine={sectionABottomLine(summary?.reached_user, summary?.blocked)}
          >
            {overview && summary && (
              <>
                <QuarterSnapshot
                  snapshot={overview.quarter_snapshot}
                  definition={overview.definition.statement}
                />
                <BlockedTrend
                  points={overview.trend}
                  windowDays={summary.window_days}
                  generatedAt={overview.generated_at}
                />
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <ClassificationDonut
                    slices={overview.classification_mix}
                    blockedTotal={summary.blocked}
                    windowDays={summary.window_days}
                    generatedAt={overview.generated_at}
                  />
                  <BlockRateMeter summary={summary} />
                </div>
              </>
            )}
          </BoardSection>

          <BoardSection
            id="security-under-pressure"
            eyebrow="Section B"
            title="Under pressure"
            lead="Who is being aimed at, by whom, and whether the mail is even flowing — the volume context that decides whether a quiet week is good news."
            bottomLine="Attack pressure lands on named people, attributed campaigns and a small number of cohorts. Volume is the control on the story: a collapse here would make every calm number above meaningless."
          >
            {overview && summary && (
              <CampaignTimeline
                campaigns={overview.campaigns}
                actors={summary.actors}
                windowDays={summary.window_days}
              />
            )}

            {/* The identity payload is its own query: role-gated, short-lived
                in cache and never persisted. Until it arrives, the panel is
                simply absent rather than rendered as an empty list. */}
            {overview && targeted && (
              <TargetedPeople
                payload={targeted}
                points={overview.vap_trend}
                lastBatchDay={overview.health.last_daily_batch_day}
              />
            )}

            {/* Where the pressure lands. Cohorts sit below the named people
                because they answer a coarser question, and above the volume
                panels because they are still about the attack rather than
                about the pipe. */}
            {overview && summary && (
              <TargetedCohorts
                rows={overview.departments}
                summary={summary}
                generatedAt={overview.generated_at}
              />
            )}

            {/* Availability first among the volume panels: if mail flow has
                collapsed, nothing above means what it appears to mean. */}
            {overview && summary && (
              <MailFlow
                points={overview.mail_flow}
                summary={summary}
                figuresCurrent={figuresCurrent}
              />
            )}

            {overview && summary && <MessageTrend points={overview.trend} summary={summary} />}
          </BoardSection>

          <BoardSection
            id="security-our-plan"
            eyebrow="Section C"
            title="Our plan"
            lead="Whether this board can be believed, and where the 2026 programme stands. The programme figures are reported by their owners, not measured here."
            bottomLine="One decision is open: leadership endorsement of the supplier security clauses for 24/7 monitoring, ahead of the September CRA reporting start."
          >
            {health && <IntegrationHealth health={health} />}
            <ProgrammeStatus />
          </BoardSection>
        </>
      ) : (
        overview && <NotConfigured overview={overview} />
      )}
    </main>
  );
}

/** The section's so-what, from the two numbers the deck card leads with. */
function sectionABottomLine(reachedUser: number | undefined, blocked: number | undefined): string {
  if (reachedUser === undefined || blocked === undefined) {
    return "Nothing measured for this window yet, so the deck card cannot be quoted from it.";
  }
  if (reachedUser === 0) {
    return `${formatCount(blocked)} dangerous emails stopped before delivery and zero incidents with business impact. The investment continues to pay off.`;
  }
  return `${formatCount(blocked)} dangerous emails stopped, but ${formatCount(reachedUser)} reached a user. That is the number to take to the leadership team, not the blocked count.`;
}
